import { OfferFeasibilityVerdict, TimelineStatusLevel } from '@codezo/graphql';
import { AlertTriangle, CheckCircle2, Clock, Truck, XCircle, type LucideIcon } from 'lucide-react';
import { formatArrivalWindow, formatDate } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';

/**
 * One anchored range — a real duration, not a blend of two scenarios — carrying
 * the verdict judged on *these* dates. Read the verdict from the range you
 * display; the enclosing object's is the conservative one and pairing it with
 * these dates prints a contradiction.
 */
type ProjectedRange = { earliest: string; latest: string; verdict: OfferFeasibilityVerdict };

interface FeasibilityBadgeProps {
	verdict: OfferFeasibilityVerdict;
	/**
	 * Projected window from the API (null when production time is unknown).
	 *
	 * Two single-anchor ranges, not one blended pair. `ifAcceptedNow` is the lead
	 * time on its own; `ifAcceptedAtDeadline` is the same width shifted by the
	 * accept-by date, and is null when there is no accept-by (it would be
	 * identical). The deprecated `earliest`/`latest` fused the two, so the range
	 * couldn't be read as a duration — kept only as a fallback while callers
	 * migrate.
	 */
	projected?: {
		ifAcceptedNow?: ProjectedRange | null;
		ifAcceptedAtDeadline?: ProjectedRange | null;
		earliest?: string | null;
		latest?: string | null;
	} | null;
	/** The deadline being judged against (item deadline, else quote deadline). */
	deadline?: string | null;
	/** Whether the window is a delivery arrival or a production-only completion. */
	kind?: 'arrival' | 'production';
	/**
	 * Colour source, when the API supplies one. `timelineStatus.level` already
	 * merges deadline urgency with achievability, so it — not the verdict —
	 * decides the tone wherever it's available. Notably `UNKNOWN` means
	 * "unverified", not "fine", so it renders neutral and never green.
	 *
	 * The verdict still picks the copy and the icon.
	 */
	level?: TimelineStatusLevel | null;
	/** Powers the hover breakdown — the facts behind the verdict. */
	breakdown?: FeasibilityBreakdown | null;
	/**
	 * The production window behind an arrival, when the caller has it. Turns
	 * "make 10–14 + ship 10–14" into the dates production actually lands on, so
	 * the split is readable rather than arithmetic.
	 */
	production?: FeasibilityBadgeProps['projected'];
	/**
	 * The lead times behind the dates, when the caller knows them.
	 *
	 * Not read off `breakdown`, which carries the same figures but only when a
	 * deadline exists — it is computed to EXPLAIN a verdict, and a quote with no
	 * deadline has no verdict, so it comes back null and takes the durations with
	 * it. Every caller already holds these numbers.
	 */
	days?: {
		productionMin?: number | null;
		productionMax?: number | null;
		transitMin?: number | null;
		transitMax?: number | null;
	} | null;
}

/**
 * Plain-language tag for a range's own verdict, shown beside its dates and
 * coloured to match.
 *
 * The verdict, not a `timelineStatus.level` — a `ProjectedDateRange` carries
 * only a verdict, and it's the one judged on exactly these dates. The pill's
 * tone still comes from the level, which is the conservative merge across both
 * scenarios; that's why the pill can be amber while "accepted now" reads green.
 */
const VERDICT_NOTE: Record<OfferFeasibilityVerdict, { text: string; className: string } | null> = {
	[OfferFeasibilityVerdict.Ok]: { text: 'on track', className: 'text-status-success' },
	[OfferFeasibilityVerdict.MaySlip]: { text: 'might miss', className: 'text-status-warn' },
	[OfferFeasibilityVerdict.Infeasible]: { text: "can't meet", className: 'text-status-danger' },
	[OfferFeasibilityVerdict.NoDeadline]: null,
	[OfferFeasibilityVerdict.ProductionUnknown]: null,
};

/** Ranking so the badge can tone on the worse of the two accept scenarios. */
const SEVERITY: Record<OfferFeasibilityVerdict, number> = {
	[OfferFeasibilityVerdict.NoDeadline]: 0,
	[OfferFeasibilityVerdict.ProductionUnknown]: 0,
	[OfferFeasibilityVerdict.Ok]: 1,
	[OfferFeasibilityVerdict.MaySlip]: 2,
	[OfferFeasibilityVerdict.Infeasible]: 3,
};

const LEVEL_TONES: Record<TimelineStatusLevel, keyof typeof TONES> = {
	[TimelineStatusLevel.OnTrack]: 'success',
	[TimelineStatusLevel.AtRisk]: 'warn',
	[TimelineStatusLevel.Critical]: 'danger',
	[TimelineStatusLevel.Unknown]: 'neutral',
};

/** The ingredients behind a verdict (`OfferFeasibilityBreakdown`) — facts only. */
type FeasibilityBreakdown = {
	anchorDate?: string | null;
	minProductionDays?: number | null;
	maxProductionDays?: number | null;
	deliveryDaysMin?: number | null;
	deliveryDaysMax?: number | null;
	leadTimeDaysMin?: number | null;
	leadTimeDaysMax?: number | null;
	projectedArrivalEarliest?: string | null;
	projectedArrivalLatest?: string | null;
	overshootBusinessDays?: number | null;
	overshootBestCaseBusinessDays?: number | null;
	deadlineCapacityBusinessDays?: number | null;
};

const TONES = {
	success: 'bg-status-success/10 text-status-success',
	warn: 'bg-status-warn/10 text-status-warn',
	danger: 'bg-status-danger/10 text-status-danger',
	neutral: 'bg-surface-fill text-text-2 ring-1 ring-inset ring-line-2',
} as const;

/**
 * Renders a feasibility verdict as a colored pill. The backend owns the verdict;
 * the frontend owns the copy, colour and icon.
 *
 * The badge states `ifAcceptedNow` — the lead time on its own, which is the
 * question "when does this arrive" actually asks.
 *
 * The verdict is read off THAT range, never off the enclosing object. The
 * enclosing verdict is the conservative one, covering the case where the client
 * waits, so pairing it with these dates prints a contradiction: "Might miss
 * Sep 17 · arrives Aug 24 – Sep 10", warning about a date that clears.
 *
 * Waiting until the accept-by shifts the range later by the same amount, and its
 * own verdict may be worse. When it is, that difference is appended — it's the
 * cost of the client sitting on the offer, and the most useful thing here.
 */
export function FeasibilityBadge({
	verdict,
	projected,
	deadline,
	kind = 'arrival',
	level,
	breakdown,
	production,
	days,
}: FeasibilityBadgeProps) {
	// Fall back to the deprecated blended pair until every caller has migrated.
	const now =
		projected?.ifAcceptedNow ??
		(projected?.earliest && projected?.latest
			? { earliest: projected.earliest, latest: projected.latest, verdict }
			: null);
	const atDeadline = projected?.ifAcceptedAtDeadline ?? null;

	const window = formatArrivalWindow(now?.earliest, now?.latest);
	const waitedWindow = formatArrivalWindow(atDeadline?.earliest, atDeadline?.latest);

	const isProduction = kind === 'production';
	const verb = isProduction ? 'Ready' : 'Arrives';

	// The rule: the verdict must come from the range being displayed.
	const shown = now?.verdict ?? verdict;

	/**
	 * Colour and icon follow the WORSE of the two accept scenarios, not the one
	 * whose dates are printed.
	 *
	 * A plan that lands comfortably if accepted today but risks the deadline if
	 * the client sits on it is not a green situation, and the resting badge is
	 * often all anyone reads. The dates stay on "accepted today" — that's the
	 * question being asked — and the hover shows both.
	 */
	const worst =
		atDeadline && SEVERITY[atDeadline.verdict] > SEVERITY[shown] ? atDeadline.verdict : shown;

	let tone: keyof typeof TONES;
	let Icon: LucideIcon;

	switch (worst) {
		case OfferFeasibilityVerdict.Ok:
			tone = 'success';
			Icon = CheckCircle2;
			break;
		case OfferFeasibilityVerdict.MaySlip:
			tone = 'warn';
			Icon = AlertTriangle;
			break;
		case OfferFeasibilityVerdict.Infeasible:
			tone = 'danger';
			Icon = XCircle;
			break;
		case OfferFeasibilityVerdict.ProductionUnknown:
			tone = 'neutral';
			Icon = Clock;
			break;
		case OfferFeasibilityVerdict.NoDeadline:
		default:
			tone = 'neutral';
			Icon = isProduction ? Clock : Truck;
			break;
	}

	/**
	 * At rest the badge is the date and nothing else — the colour and icon carry
	 * the verdict, and the hover breakdown carries the reasoning.
	 *
	 * It used to spell out the deadline, the window and the cost of waiting all at
	 * once, which produced the same long sentence on every tier row of a product
	 * and buried the one thing the column is for.
	 */
	const label = window
		? `${verb} ${window}`
		: shown === OfferFeasibilityVerdict.ProductionUnknown
			? 'Set production days'
			: shown === OfferFeasibilityVerdict.Infeasible
				? deadline
					? `Can't meet ${formatDate(deadline)}`
					: "Can't meet deadline"
				: shown === OfferFeasibilityVerdict.MaySlip
					? deadline
						? `Might miss ${formatDate(deadline)}`
						: 'Might slip'
					: 'No timeline';

	// The level, when given, overrides the verdict-derived tone — it's the
	// backend's own merge of urgency and achievability.
	if (level) tone = LEVEL_TONES[level];

	/**
	 * The hover breakdown, grouped by when the client accepts.
	 *
	 * Both scenarios show the same two milestones, so they can be read against
	 * each other — the gap between the groups is exactly what waiting costs.
	 * Mixing them (production and arrival from one anchor, then a lone arrival
	 * from the other) made them look like different kinds of fact.
	 *
	 * Day counts are deliberately absent: they're already on the tier row and the
	 * delivery fields, and adding them up to reach a date is the work this saves.
	 */
	const acceptBy = breakdown?.anchorDate;
	/* The caller's own figures first; the breakdown only as a fallback, and it is
	   populated only with a deadline set. */
	const productionDays = formatDays(
		days?.productionMin ?? breakdown?.minProductionDays,
		days?.productionMax ?? breakdown?.maxProductionDays
	);
	const transitDays = formatDays(
		days?.transitMin ?? breakdown?.deliveryDaysMin,
		days?.transitMax ?? breakdown?.deliveryDaysMax
	);
	const readyNow = formatArrivalWindow(
		production?.ifAcceptedNow?.earliest,
		production?.ifAcceptedNow?.latest
	);
	const readyWaited = formatArrivalWindow(
		production?.ifAcceptedAtDeadline?.earliest,
		production?.ifAcceptedAtDeadline?.latest
	);

	/* Rendered whenever there is anything to say, not only when there are dates: a
	   tier with production days but no delivery lead time yet has no window, and
	   the days are exactly what is worth knowing in that state. */
	const detail =
		window || productionDays || transitDays ? (
			<div className="grid min-w-[310px] grid-cols-[auto_1fr] items-baseline gap-x-10 gap-y-1 text-xs">
				{window && (
					<ScenarioRows
						label="If accepted today"
						ready={isProduction ? null : readyNow}
						arrival={window}
						arrivalLabel={isProduction ? 'Production ready' : 'Arrives'}
						note={now ? VERDICT_NOTE[now.verdict] : null}
					/>
				)}
				{waitedWindow && atDeadline && (
					<ScenarioRows
						label={acceptBy ? `If accepted ${formatDate(acceptBy)}` : 'If they wait'}
						ready={isProduction ? null : readyWaited}
						arrival={waitedWindow}
						arrivalLabel={isProduction ? 'Production ready' : 'Arrives'}
						note={VERDICT_NOTE[atDeadline.verdict]}
					/>
				)}
				{window && (productionDays || transitDays || deadline) && (
					<span className="col-span-2 mt-1 border-t border-line-2" />
				)}
				{productionDays && (
					<>
						<span className="opacity-60">Production time</span>
						<span className="text-right whitespace-nowrap">{productionDays}</span>
					</>
				)}
				{/* Only on an arrival: a production badge's dates already ARE the end of
			    production, so transit is not part of what it is claiming. */}
				{!isProduction && transitDays && (
					<>
						<span className="opacity-60">Transit time</span>
						<span className="text-right whitespace-nowrap">{transitDays}</span>
					</>
				)}
				{deadline && (
					<>
						<span className="opacity-60">Deadline</span>
						<span className="text-right whitespace-nowrap">{formatDate(deadline)}</span>
					</>
				)}
			</div>
		) : null;

	const pill = (
		<span
			className={`inline-flex w-fit items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium ${TONES[tone]}`}
		>
			<Icon size={11} strokeWidth={2.5} />
			{label}
		</span>
	);

	if (!detail) return pill;

	return (
		// Self-contained provider: these badges render all over the app, well
		// outside the one in PlatformLayout, and Base UI needs a provider in scope
		// for the popup to open.
		<TooltipProvider delay={200}>
			<Tooltip>
				{/* A span, not the default button — some of these pills sit inside
				    clickable rows, and a nested button would be invalid. tabIndex
				    keeps it reachable by keyboard, since the content is the only
				    place the two accept scenarios are spelled out. */}
				<TooltipTrigger render={<span className="w-fit" tabIndex={0} />}>
					{pill}
				</TooltipTrigger>
				<TooltipContent surface="panel" className="block max-w-none px-3.5 py-2.5">
					{detail}
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}

/**
 * The breakdown is one grid, not a stack of rows, so every value lines up on the
 * same edge regardless of how long its label is. Per-row flex left it ragged.
 */
/**
 * A day range as a duration — "10–14 days", or "1 day" when both ends agree.
 *
 * Null unless BOTH ends are known: half a range is not a duration, and printing
 * one end as though it were would promise a lead time nobody quoted.
 */
function formatDays(min?: number | null, max?: number | null): string | null {
	if (min == null || max == null) return null;
	if (min === max) return `${min} ${min === 1 ? 'day' : 'days'}`;
	return `${min}–${max} days`;
}

function ScenarioRows({
	label,
	ready,
	arrival,
	arrivalLabel,
	note,
}: {
	label: string;
	ready?: string | null;
	arrival: string;
	arrivalLabel: string;
	note?: { text: string; className: string } | null;
}) {
	return (
		<>
			<span className="col-span-2 pt-1 text-2xs font-medium tracking-wide opacity-45 first:pt-0">
				{label}
			</span>
			{ready && (
				<>
					<span className="opacity-60">Production ready</span>
					<span className="text-right whitespace-nowrap">{ready}</span>
				</>
			)}
			<span className="opacity-60">{arrivalLabel}</span>
			<span className="text-right whitespace-nowrap">
				{arrival}
				{note && <span className={`font-medium ${note.className}`}> · {note.text}</span>}
			</span>
		</>
	);
}
